import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { withRouter } from '../../../common/withRouter';
import { ButtonOBSOLETED, Notification, Message } from "../../../web.common";
import { LayoutContext } from "../../layout/siteLayout";
import PageTop from "../../layout/PageTop";
import withApi from "../../higherOrderComponents/withApi";
import { apiErrorText, clubhouseFormError, panelStyle, buttonRowStyle, Pager, warningTextStyle, PlaceField, isFacilityKind, placeText, placeMissing, placePayload, placeReady, emptyExactPlace } from "./shared";

const pageSize = 50;

const clubhouseKinds = ["club", "area", "facility", "house", "circle"];

const emptyClubhouse = { name: "", slug: "", kind: "club", areaSlug: "", facilitySlug: "", ...emptyExactPlace, subtitle: "", description: "" };

// "Nordic Padel Club!" -> "nordic-padel-club" (accents removed, anything else non-alphanumeric becomes a hyphen).
const slugFromName = (name) =>
  name
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);

const clubhouseStatus = (clubhouse) => {
  if (clubhouse.deletedAtUtc) return "deleted";
  return clubhouse.isOpen ? "open" : "closed";
};

const ClubhousesInternal = ({ api }) => {
  const [clubhouses, setClubhouses] = useState(null);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [newClubhouse, setNewClubhouse] = useState(emptyClubhouse);
  const [slugEdited, setSlugEdited] = useState(false);
  const [isBusy, setIsBusy] = useState(false);
  const [notification, setNotification] = useState(null);

  const loadData = async (nextSkip = skip, withDeleted = includeDeleted) => {
    try {
      const result = await api.clubhouseAdmin.getClubhouses({ includeDeleted: withDeleted, take: pageSize, skip: nextSkip });
      setClubhouses(result.clubhouses);
      setTotal(result.total);
    } catch (e) {
      setNotification(new Message("error", apiErrorText(e, "Unable to load the clubhouses.")));
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const changePage = (nextSkip) => {
    setSkip(nextSkip);
    loadData(nextSkip);
  };

  const toggleDeleted = ({ target }) => {
    setIncludeDeleted(target.checked);
    setSkip(0);
    loadData(0, target.checked);
  };

  const updateNewClubhouse = (field, value) => {
    setNewClubhouse((current) => {
      const next = { ...current, [field]: value };
      if (field === 'name' && !slugEdited) next.slug = slugFromName(value);
      return next;
    });
  };

  const createClubhouse = async () => {
    const invalid = clubhouseFormError(newClubhouse, { checkSlug: true });
    if (invalid) {
      setNotification(new Message("error", invalid));
      return;
    }
    setIsBusy(true);
    try {
      const created = await api.clubhouseAdmin.createClubhouse({
        name: newClubhouse.name,
        slug: newClubhouse.slug,
        kind: newClubhouse.kind,
        ...placePayload(newClubhouse.kind, newClubhouse),
        subtitle: newClubhouse.subtitle || null,
        description: newClubhouse.description || null,
      });
      setNotification(new Message("success", `Clubhouse "${created.name}" created (${created.slug}).`));
      setIsCreating(false);
      setNewClubhouse(emptyClubhouse);
      setSlugEdited(false);
      loadData();
    } catch (e) {
      setNotification(new Message("error", apiErrorText(e, "Unable to create the clubhouse.")));
    } finally {
      setIsBusy(false);
    }
  };

  return (
    <LayoutContext.Consumer>
      {() => (
        <React.Fragment>
          <Notification newMessage={notification} />
          <div className="content-with-right-pane-delimiter">
            <PageTop documentTitle="Clubhouses" documentDescription="Clubhouses" />
            <h2 className="main-page-header">Clubhouses</h2>

            <div style={{ display: "flex", gap: "1.5rem", alignItems: "center", flexWrap: "wrap" }}>
              <label>
                <input type="checkbox" checked={includeDeleted} onChange={toggleDeleted} /> Include deleted
              </label>
              <Link to="/clubhouse-wannas">Wannas</Link>
              <Link to="/clubhouse-memberships">Memberships</Link>
              <Link to="/clubhouse-payments">Payments</Link>
              <ButtonOBSOLETED buttonContent="New clubhouse" disabled={isBusy} onClick={() => setIsCreating(true)} />
            </div>

            {isCreating && (
              <div style={panelStyle}>
                <h3>New clubhouse</h3>
                <div style={{ display: "grid", gap: "0.75rem", maxWidth: "32rem" }}>
                  <label>Name
                    <input type="text" value={newClubhouse.name} onChange={(e) => updateNewClubhouse('name', e.target.value)} maxLength={80} style={{ display: "block", width: "100%" }} />
                  </label>
                  <label>Slug (lowercase letters, digits and hyphens)
                    <input type="text" value={newClubhouse.slug} onChange={(e) => { setSlugEdited(true); updateNewClubhouse('slug', e.target.value); }} maxLength={60} style={{ display: "block", width: "100%" }} />
                  </label>
                  <label>Kind
                    <select value={newClubhouse.kind} onChange={(e) => updateNewClubhouse('kind', e.target.value)} style={{ display: "block", width: "100%" }}>
                      {clubhouseKinds.map((kind) => <option key={kind} value={kind}>{kind}</option>)}
                    </select>
                  </label>
                  <p style={{ fontSize: "0.9em", margin: 0 }}>
                    {isFacilityKind(newClubhouse.kind) || newClubhouse.kind === "area" ? "Open: anyone can join." : "Closed: members only (joining by link)."}
                  </p>
                  <PlaceField
                    api={api}
                    kind={newClubhouse.kind}
                    place={newClubhouse}
                    onChange={({ areaSlug, facilitySlug, addressText, latitude, longitude }) => setNewClubhouse((current) => ({ ...current, areaSlug, facilitySlug, addressText, latitude, longitude }))}
                  />
                  <label>Subtitle (optional)
                    <input type="text" value={newClubhouse.subtitle} onChange={(e) => updateNewClubhouse('subtitle', e.target.value)} maxLength={200} style={{ display: "block", width: "100%" }} />
                  </label>
                  <label>Description (optional)
                    <textarea value={newClubhouse.description} onChange={(e) => updateNewClubhouse('description', e.target.value)} maxLength={2000} rows={3} style={{ display: "block", width: "100%" }} />
                  </label>
                </div>
                <p style={{ fontSize: "0.9em" }}>The creator is not added as a member; members join the clubhouse afterwards.</p>
                <div style={buttonRowStyle}>
                  <ButtonOBSOLETED buttonContent="Create clubhouse" disabled={isBusy || !newClubhouse.name || !newClubhouse.slug || !placeReady(newClubhouse.kind, newClubhouse)} onClick={createClubhouse} />
                  <ButtonOBSOLETED buttonContent="Cancel" disabled={isBusy} onClick={() => { setIsCreating(false); setNewClubhouse(emptyClubhouse); setSlugEdited(false); }} />
                </div>
              </div>
            )}

            <br />
            {clubhouses == null ? (
              <span>Loading...</span>
            ) : (
              <article className="data-table-wrap">
                <table className="data-lists component-data-table cell-content-align-top">
                  <thead>
                    <tr>
                      <td>Name</td>
                      <td>Slug</td>
                      <td>Kind</td>
                      <td>Area / facility</td>
                      <td>Status</td>
                      <td>Members</td>
                      <td>Wannas</td>
                      <td>Created</td>
                    </tr>
                  </thead>
                  <tbody>
                    {clubhouses.map((clubhouse) => {
                      return (
                        <tr key={clubhouse.slug}>
                          <td><Link to={`/clubhouse/${encodeURIComponent(clubhouse.slug)}`}><strong>{clubhouse.name}</strong></Link></td>
                          <td>{clubhouse.slug}</td>
                          <td>{clubhouse.kind}</td>
                          <td style={placeMissing(clubhouse) ? warningTextStyle : undefined}>{placeText(clubhouse)}</td>
                          <td>{clubhouseStatus(clubhouse)}</td>
                          <td>{clubhouse.liveMemberCount}</td>
                          <td>{clubhouse.wannaCount}</td>
                          <td>{clubhouse.createdAtUtc ? clubhouse.createdAtUtc.substring(0, 10) : "-"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </article>
            )}
            <Pager skip={skip} take={pageSize} total={clubhouses ? total : null} onChange={changePage} />
          </div>
        </React.Fragment>
      )}
    </LayoutContext.Consumer>
  );
};

const Clubhouses = withRouter(
  withApi(
    connect(
      ({ user }) => ({ isLoggedIn: !!user }),
      {}
    )(ClubhousesInternal)
  )
);

Clubhouses.requiresLogin = true;
Clubhouses.propTypes = { isLoggedIn: PropTypes.bool };

export default Clubhouses;
